// SarLab API Service
// This module connects the frontend to the SarLab backend
// Token management is handled via HttpOnly cookies set by the server

const API_BASE_URL = process.env.REACT_APP_API_URL;
const TENANT_CODE = process.env.REACT_APP_TENANT_CODE;

// Security: Track refresh attempts to prevent infinite loops
let refreshAttemptCount = 0;
const MAX_REFRESH_ATTEMPTS = 1;

// API request helper - uses cookies for authentication
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  isRetry = false
): Promise<{ success: boolean; data?: T; error?: string; code?: string }> {
  const url = `${API_BASE_URL}/api/public${endpoint}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Add tenant code header for multi-tenant support
  if (TENANT_CODE) {
    headers['X-Tenant'] = TENANT_CODE;
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include', // Send cookies with requests
    });

    const data = await response.json();

    if (!response.ok) {
      // If token expired, try to refresh (only once per request chain)
      if (response.status === 401 && data.code === 'TOKEN_EXPIRED' && !isRetry) {
        // Security: Prevent infinite refresh loops
        if (refreshAttemptCount < MAX_REFRESH_ATTEMPTS) {
          refreshAttemptCount++;
          const refreshed = await refreshAccessToken();
          refreshAttemptCount--;

          if (refreshed) {
            // Retry the request with refreshed cookie (mark as retry to prevent loops)
            return apiRequest(endpoint, options, true);
          }
        }
      }
      return { success: false, error: data.error, code: data.code };
    }

    return { success: true, data };
  } catch (error) {
    console.error('API request error:', error);
    return { success: false, error: 'Network error', code: 'NETWORK_ERROR' };
  }
}

// Refresh token using HttpOnly cookie
async function refreshAccessToken(): Promise<boolean> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (TENANT_CODE) {
    headers['X-Tenant'] = TENANT_CODE;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/public/auth/refresh`, {
      method: 'POST',
      headers,
      credentials: 'include', // Send refresh token cookie
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    return data.success === true;
  } catch {
    return false;
  }
}

// ============================================
// AUTH API
// ============================================

export interface RegisterData {
  email: string;
  username: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  tcKimlikNo: string;
  phone: string;
  dateOfBirth: string;
  referralCode?: string;
}

export interface LoginData {
  emailOrUsername: string;
  password: string;
}

export interface Player {
  id: string;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  phone?: string;
  dateOfBirth?: string;
  tcKimlikNo?: string;
  status: 'active' | 'suspended' | 'banned';
  kycStatus: 'none' | 'pending' | 'verified' | 'rejected';
  vipLevel: number;
  emailVerified?: boolean;
  phoneVerified?: boolean;
  createdAt?: string;
}

export interface PlayerProfile {
  country?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  occupation?: string;
  currency?: string;
  kycInfoSubmittedAt?: string;
  kycInfoVerifiedAt?: string;
}

export interface KycDocument {
  id: string;
  documentType: string;
  fileName: string;
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason?: string;
  createdAt: string;
}

export interface Wallet {
  balance: number;
  currency: string;
}

export interface AuthResponse {
  success: boolean;
  player?: Player;
  wallet?: Wallet;
}

export const authApi = {
  register: async (data: RegisterData) => {
    return apiRequest<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  login: async (data: LoginData) => {
    return apiRequest<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  logout: async () => {
    return apiRequest('/auth/logout', { method: 'POST' });
  },

  me: async () => {
    return apiRequest<AuthResponse>('/auth/me');
  },

  // Check if user might be logged in (has session cookie - actual validation done server-side)
  checkSession: async (): Promise<boolean> => {
    const result = await apiRequest<AuthResponse>('/auth/me');
    return result.success && !!result.data?.player;
  },
};

// ============================================
// PROMOTIONS API
// ============================================

export interface Promotion {
  id: string;
  title: string;
  slug: string;
  shortDescription?: string;
  description?: string;
  imageUrl?: string;
  type: 'deposit' | 'cashback' | 'freespin' | 'reload' | 'welcome' | 'vip' | 'custom';
  value?: number;
  valueType: 'fixed' | 'percentage';
  minDeposit?: number;
  maxBonus?: number;
  wagerMultiplier?: number;
  validFrom?: string;
  validTo?: string;
  isFeatured: boolean;
  terms?: string;
}

export interface PromotionsResponse {
  success: boolean;
  promotions: Promotion[];
  total: number;
}

export const promotionsApi = {
  list: async (options?: { featured?: boolean; type?: string; limit?: number }) => {
    const params = new URLSearchParams();
    if (options?.featured) params.append('featured', 'true');
    if (options?.type) params.append('type', options.type);
    if (options?.limit) params.append('limit', options.limit.toString());
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiRequest<PromotionsResponse>(`/promotions${query}`);
  },

  getBySlug: async (slug: string) => {
    return apiRequest<{ success: boolean; promotion: Promotion }>(`/promotions/${slug}`);
  },
};

// ============================================
// TOURNAMENTS API
// ============================================

export interface Tournament {
  id: string;
  name: string;
  slug: string;
  description?: string;
  imageUrl?: string;
  bannerUrl?: string;
  prizePool: number;
  prizeDistribution?: Record<string, number>;
  entryFee: number;
  minLevel: number;
  maxParticipants?: number;
  currentParticipants: number;
  startDate: string;
  endDate: string;
  status: 'draft' | 'upcoming' | 'active' | 'finished' | 'cancelled';
  isFeatured: boolean;
}

export interface TournamentsResponse {
  success: boolean;
  tournaments: Tournament[];
  total: number;
}

export const tournamentsApi = {
  list: async (options?: { featured?: boolean; status?: string; limit?: number }) => {
    const params = new URLSearchParams();
    if (options?.featured) params.append('featured', 'true');
    if (options?.status) params.append('status', options.status);
    if (options?.limit) params.append('limit', options.limit.toString());
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiRequest<TournamentsResponse>(`/tournaments${query}`);
  },

  getBySlug: async (slug: string) => {
    return apiRequest<{ success: boolean; tournament: Tournament }>(`/tournaments/${slug}`);
  },

  getLeaderboard: async (slug: string) => {
    return apiRequest<{
      success: boolean;
      leaderboard: Array<{
        rank: number;
        playerId: string;
        username: string;
        score: number;
        prize?: number;
      }>;
    }>(`/tournaments/${slug}/leaderboard`);
  },

  join: async (slug: string) => {
    return apiRequest<{ success: boolean }>(`/tournaments/${slug}/join`, { method: 'POST' });
  },
};

// ============================================
// BANNERS API
// ============================================

export interface Banner {
  id: string;
  title: string;
  imageUrl: string;
  linkUrl?: string;
  position: string;
}

export interface BannersResponse {
  success: boolean;
  banners: Banner[];
}

export const bannersApi = {
  list: async (position?: string) => {
    const query = position ? `?position=${position}` : '';
    return apiRequest<BannersResponse>(`/banners${query}`);
  },
};

// ============================================
// GAMES API
// ============================================

export interface GameProvider {
  id: string;
  code: string;
  name: string;
}

export interface Game {
  id: string;
  name: string;
  category: string;
  provider: string; // Provider name for display
  thumbnailUrl?: string;
  rtp?: number;
  devices?: string;
}

export interface GamesResponse {
  success: boolean;
  games: Game[];
  pagination?: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
  providers?: GameProvider[];
}

// Helper to transform raw API game to Game interface
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const transformGame = (raw: any): Game => ({
  id: raw.id,
  name: raw.name,
  category: raw.category,
  provider: typeof raw.provider === 'object' ? raw.provider.name : raw.provider,
  thumbnailUrl: raw.thumbnailUrl,
  rtp: raw.rtp,
  devices: raw.devices,
});

export const gamesApi = {
  list: async (options?: {
    category?: string;
    provider?: string;
    providerId?: string;
    limit?: number;
    offset?: number;
  }) => {
    const params = new URLSearchParams();
    if (options?.category) params.append('category', options.category);
    if (options?.providerId) params.append('providerId', options.providerId);
    if (options?.provider) params.append('providerId', options.provider);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    const query = params.toString() ? `?${params.toString()}` : '';

    const result = await apiRequest<{ success: boolean; games: unknown[]; pagination?: GamesResponse['pagination']; providers?: GameProvider[] }>(`/games${query}`);

    if (result.success && result.data?.games) {
      return {
        ...result,
        data: {
          ...result.data,
          games: result.data.games.map(transformGame),
        } as GamesResponse,
      };
    }
    return result as { success: boolean; data?: GamesResponse; error?: string; code?: string };
  },

  getById: async (id: string) => {
    return apiRequest<{ success: boolean; game: Game }>(`/games/${id}`);
  },
};

// ============================================
// WALLET API
// ============================================

export interface Transaction {
  id: string;
  type: 'deposit' | 'withdrawal' | 'bet' | 'win' | 'bonus' | 'adjustment';
  amount: number;
  balanceAfter: number;
  description?: string;
  createdAt: string;
}

export const walletApi = {
  getBalance: async () => {
    return apiRequest<{ success: boolean; wallet: Wallet }>('/player/wallet');
  },

  getTransactions: async (options?: { type?: string; limit?: number; offset?: number }) => {
    const params = new URLSearchParams();
    if (options?.type) params.append('type', options.type);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiRequest<{ success: boolean; transactions: Transaction[]; total: number }>(
      `/player/transactions${query}`
    );
  },
};

// ============================================
// DEPOSIT API
// ============================================

export interface PaymentGateway {
  code: string;
  name: string;
  type: 'bank_transfer' | 'papara' | 'crypto';
  isActive: boolean;
  minDepositMinor: number;
  maxDepositMinor: number | null;
  minWithdrawMinor: number;
  maxWithdrawMinor: number | null;
  feeMinor: number;
  feeType: 'fixed' | 'percentage';
}

export interface BankAccount {
  id: string;
  bankCode: string;
  bankName: string;
  accountHolder: string;
  iban: string;
  description: string | null;
  isActive: boolean;
  sortOrder: number;
}

export interface DepositMethodsResponse {
  gateways: PaymentGateway[];
  bankAccounts: BankAccount[];
}

export interface DepositPaymentInfo {
  type: string;
  bankAccount?: {
    bankName: string;
    accountHolder: string;
    iban: string;
  };
  redirectUrl?: string;
  cryptoAddress?: string;
  cryptoNetwork?: string;
  cryptoAmount?: string;
  qrCodeData?: string;
  instructions?: string;
}

export interface DepositResponse {
  success: boolean;
  transactionId: string;
  status: string;
  gatewayCode: string;
  amountMinor: number;
  feeMinor: number;
  netAmountMinor: number;
  expiresAt: string | null;
  paymentInfo?: DepositPaymentInfo;
}

export interface Deposit {
  id: string;
  gatewayCode: string;
  amountMinor: number;
  feeMinor: number;
  netAmountMinor: number;
  currency: string;
  status: 'pending' | 'confirmed' | 'credited' | 'failed' | 'expired';
  createdAt: string;
  confirmedAt: string | null;
  creditedAt: string | null;
  expiresAt: string | null;
}

export const depositApi = {
  getMethods: async () => {
    return apiRequest<DepositMethodsResponse>('/player/deposit/methods');
  },

  create: async (data: {
    gatewayCode: string;
    amountMinor: number;
    currency?: string;
    bankAccountId?: string;
    idempotencyKey?: string;
  }) => {
    return apiRequest<DepositResponse>('/player/deposit', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getHistory: async (options?: { status?: string; limit?: number; offset?: number }) => {
    const params = new URLSearchParams();
    if (options?.status) params.append('status', options.status);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiRequest<{ deposits: Deposit[]; total: number; limit: number; offset: number }>(
      `/player/deposit${query}`
    );
  },
};

// ============================================
// WITHDRAW API
// ============================================

export interface WithdrawAccountInfo {
  bankCode?: string;
  iban?: string;
  accountHolder?: string;
  cryptoAddress?: string;
  cryptoNetwork?: string;
  paparaNumber?: string;
}

export interface WithdrawResponse {
  success: boolean;
  transactionId: string;
  status: string;
  amountMinor: number;
  feeMinor: number;
  netAmountMinor: number;
  estimatedProcessingTime?: string;
}

export interface Withdrawal {
  id: string;
  gatewayCode: string;
  amountMinor: number;
  feeMinor: number;
  netAmountMinor: number;
  currency: string;
  status: 'pending' | 'approved' | 'processing' | 'completed' | 'rejected' | 'cancelled';
  createdAt: string;
  approvedAt: string | null;
  completedAt: string | null;
  rejectedReason: string | null;
}

export const withdrawApi = {
  create: async (data: {
    gatewayCode: string;
    amountMinor: number;
    currency?: string;
    accountInfo: WithdrawAccountInfo;
    idempotencyKey?: string;
  }) => {
    return apiRequest<WithdrawResponse>('/player/withdraw', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getHistory: async (options?: { status?: string; limit?: number; offset?: number }) => {
    const params = new URLSearchParams();
    if (options?.status) params.append('status', options.status);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiRequest<{ withdrawals: Withdrawal[]; total: number; limit: number; offset: number }>(
      `/player/withdraw${query}`
    );
  },
};

// ============================================
// CHAT API
// ============================================

export interface ChatMessage {
  id: string;
  userId: string;
  username: string;
  text: string;
  type: 'text' | 'system' | 'announcement';
  isAdmin: boolean;
  timestamp: number;
}

export interface ChatRestrictions {
  isBanned: boolean;
  timeoutUntil: string | null;
}

export const chatApi = {
  getMessages: async (limit = 50) => {
    return apiRequest<{ success: boolean; messages: ChatMessage[]; restrictions: ChatRestrictions | null }>(
      `/chat/messages?limit=${limit}`
    );
  },

  sendMessage: async (content: string) => {
    return apiRequest<{ success: boolean; message: ChatMessage }>('/chat/messages', {
      method: 'POST',
      body: JSON.stringify({ message: content }),
    });
  },

  getStatus: async () => {
    return apiRequest<{ success: boolean; online: number }>('/chat/status');
  },
};

// ============================================
// RAIN API
// ============================================

export interface ActiveRain {
  id: string;
  adminName: string;
  totalAmount: string;
  maxWinners: number;
  distributionType: 'random' | 'equal';
  status: 'active' | 'completed' | 'cancelled';
  startedAt: string;
  participantCount: number;
}

export interface RainWinner {
  playerId: string;
  playerUsername: string;
  amount: number;
}

export const rainApi = {
  getActiveRain: async () => {
    return apiRequest<{ activeRain: ActiveRain | null }>('/chat/rain/active');
  },

  participate: async (rainId: string) => {
    return apiRequest<{ success: boolean; alreadyParticipated: boolean; participantCount: number }>(
      `/chat/rain/${rainId}/participate`,
      {
        method: 'POST',
        body: JSON.stringify({}),
      }
    );
  },
};

// ============================================
// SUPPORT API
// ============================================

export interface SupportTicket {
  id: string;
  odaId?: string;
  odaAdi?: string;
  subject: string;
  subjectLabel?: string;
  initialMessage?: string;
  status: 'waiting' | 'active' | 'closed';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  assignedAdminName?: string;
  createdAt: string;
  assignedAt?: string;
  updatedAt?: string;
}

export interface SupportMessage {
  id: string;
  ticketId: string;
  senderId: string;
  senderType?: 'player' | 'admin';
  senderRole?: 'player' | 'admin'; // Backend uses senderRole
  senderName?: string;
  message: string;
  content?: string; // Alias for backward compatibility
  createdAt: string;
}

export const supportApi = {
  getTickets: async () => {
    return apiRequest<{ success: boolean; ticket?: SupportTicket; tickets?: SupportTicket[]; isAuthenticated?: boolean; username?: string }>('/livechat/sessions');
  },

  createTicket: async (subject: string, message: string, name?: string, email?: string) => {
    return apiRequest<{ success: boolean; ticket: SupportTicket }>('/livechat/sessions', {
      method: 'POST',
      body: JSON.stringify({ subject, message, name, email }),
    });
  },

  getTicketMessages: async (ticketId: string) => {
    return apiRequest<{ success: boolean; messages: SupportMessage[] }>(
      `/livechat/sessions/${ticketId}/messages`
    );
  },

  sendTicketMessage: async (ticketId: string, content: string) => {
    return apiRequest<{ success: boolean; message: SupportMessage }>(
      `/livechat/sessions/${ticketId}/messages`,
      {
        method: 'POST',
        body: JSON.stringify({ content }),
      }
    );
  },

  closeTicket: async (ticketId: string) => {
    return apiRequest<{ success: boolean; message: string }>(
      `/livechat/sessions/${ticketId}`,
      {
        method: 'DELETE',
      }
    );
  },
};

// ============================================
// PROFILE API
// ============================================

export interface FullProfileResponse {
  success: boolean;
  player: Player;
  profile: PlayerProfile | null;
  documents: KycDocument[];
}

export const profileApi = {
  getFullProfile: async () => {
    return apiRequest<FullProfileResponse>('/player/profile');
  },

  updateKycInfo: async (data: {
    country?: string;
    address?: string;
    city?: string;
    postalCode?: string;
    occupation?: string;
  }) => {
    return apiRequest<{ success: boolean; message: string }>('/player/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  getKycDocuments: async () => {
    return apiRequest<{ success: boolean; documents: KycDocument[] }>('/player/kyc');
  },

  uploadKycDocument: async (file: File, documentType: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);

    const url = `${API_BASE_URL}/api/public/player/kyc`;

    const headers: Record<string, string> = {};
    if (TENANT_CODE) {
      headers['X-Tenant'] = TENANT_CODE;
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Upload error:', error);
      return { success: false, error: 'Upload failed' };
    }
  },
};

// ============================================
// CONTACT API
// ============================================

export interface ContactData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

export const contactApi = {
  send: async (data: ContactData) => {
    return apiRequest<{ success: boolean; message: string }>('/contact', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// ============================================
// CALL ME API (Beni Ara)
// ============================================

export const callMeApi = {
  request: async () => {
    return apiRequest<{ success: boolean; message: string }>('/player/call-me', {
      method: 'POST',
    });
  },
};

// ============================================
// BONUS CODES API
// ============================================

export interface BonusRedeemResponse {
  success: boolean;
  message: string;
  bonus?: {
    id: string;
    name: string;
    type: string;
    amount: number;
    wagerRequired: number;
    expiresAt: string;
  };
}

export const bonusCodesApi = {
  redeem: async (code: string, depositAmount?: number) => {
    return apiRequest<BonusRedeemResponse>('/bonus-codes/redeem', {
      method: 'POST',
      body: JSON.stringify({ code, depositAmount }),
    });
  },
};

// ============================================
// WIDGET CONFIG API
// ============================================

export interface WidgetButtonConfig {
  type: 'adaptive' | 'image' | 'text_link';
  position: 'bottom_right' | 'bottom_left' | 'top_right' | 'top_left';
  offsetX: number;
  offsetY: number;
  mobileEnabled: boolean;
  textContent: string | null;
  onlineImageUrl: string | null;
  offlineImageUrl: string | null;
}

export interface WidgetWindowConfig {
  style: 'classic' | 'modern' | 'minimal';
  primaryColor: string;
  secondaryColor: string;
  headerType: 'agent_info' | 'banner_image';
  headerText: string | null;
  bannerImageUrl: string | null;
  showAgentAvatar: boolean;
  backgroundType: 'solid' | 'texture' | 'image';
  backgroundValue: string | null;
  greetingMessage: string | null;
  windowType: 'embedded' | 'popup';
  customCss: string | null;
  autoEndChatTimeout: number;
  features: {
    soundNotifications: boolean;
    fileSend: boolean;
    emojis: boolean;
    downloadTranscript: boolean;
    printTranscript: boolean;
    emailTranscript: boolean;
    offlineSwitch: boolean;
    showUnreadDot: boolean;
    audioChat: boolean;
    videoChat: boolean;
    removePoweredBy: boolean;
  };
}

export interface WidgetPrechatField {
  id: string;
  name: string;
  label: string;
  type: 'text' | 'email' | 'phone' | 'select' | 'checkbox' | 'textarea';
  options: { value: string; label: string }[] | null;
  required: boolean;
  sortOrder: number;
}

export interface WidgetPrechatConfig {
  enabled: boolean;
  formType: 'form' | 'task_bot';
  greetingMessage: string | null;
  fields: WidgetPrechatField[];
}

export interface WidgetConfig {
  enabled: boolean;
  campaign?: {
    id: string;
    name: string;
    code: string;
    language: string;
  };
  button: WidgetButtonConfig;
  window: WidgetWindowConfig;
  prechat: WidgetPrechatConfig;
  i18n: Record<string, string>;
}

export const widgetConfigApi = {
  getConfig: async () => {
    return apiRequest<WidgetConfig>('/livechat/widget-config', {
      method: 'GET',
    });
  },
};

// ============================================
// VERIFICATION API
// ============================================

export interface VerificationStatus {
  phoneVerified: boolean;
  emailVerified: boolean;
  phoneVerifiedAt: string | null;
  emailVerifiedAt: string | null;
  isFullyVerified: boolean;
}

export interface SendCodeResponse {
  message: string;
  target: string;
}

export interface VerifyCodeResponse {
  message: string;
}

export const verificationApi = {
  getStatus: async () => {
    return apiRequest<{ data: VerificationStatus }>('/verification/status', {
      method: 'GET',
    });
  },

  sendCode: async (type: 'sms' | 'email') => {
    return apiRequest<SendCodeResponse>('/verification/send', {
      method: 'POST',
      body: JSON.stringify({ type }),
    });
  },

  verifyCode: async (type: 'sms' | 'email', code: string) => {
    return apiRequest<VerifyCodeResponse>('/verification/verify', {
      method: 'POST',
      body: JSON.stringify({ type, code }),
    });
  },
};

// Export all APIs
const api = {
  auth: authApi,
  promotions: promotionsApi,
  tournaments: tournamentsApi,
  profile: profileApi,
  banners: bannersApi,
  games: gamesApi,
  wallet: walletApi,
  deposit: depositApi,
  withdraw: withdrawApi,
  chat: chatApi,
  support: supportApi,
  contact: contactApi,
  callMe: callMeApi,
  bonusCodes: bonusCodesApi,
  widgetConfig: widgetConfigApi,
  verification: verificationApi,
};

export default api;
